import styled, { css } from 'styled-components';

/** Spec 022 UI: tokens only (`--color-*`, `--shadow-*`, `--radius-*`). */

export const SearchBox = styled.label`
  position: relative;
  display: block;
  flex: none;

  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-tertiary);
    font-size: 14px;
    pointer-events: none;
  }

  input {
    width: 100%;
    height: 42px;
    box-sizing: border-box;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    padding: 0 12px 0 36px;
    font: inherit;
    font-size: 14px;
    color: var(--color-text-primary);
    background: var(--color-bg-elevated);
  }

  input::placeholder {
    color: var(--color-text-tertiary);
  }

  input:focus {
    outline: none;
    box-shadow: var(--shadow-focus);
    border-color: var(--color-border-strong);
  }
`;

export const FromLine = styled.p`
  margin: 0;
  font-size: 12px;
  color: var(--color-text-secondary);
`;

export const Row = styled.button<{ $tall?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: ${({ $tall }) => ($tall ? '48px' : '44px')};
  padding: 6px 10px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text-primary);
  font: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--color-bg-secondary);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &[aria-selected='true'] {
    background: var(--color-black);
    color: var(--color-on-primary);
  }

  &[aria-selected='true'] .muted {
    color: inherit;
    opacity: 0.8;
  }

  mark {
    background: transparent;
    color: inherit;
    font-weight: 700;
  }
`;

export const RowName = styled.span`
  flex: 1;
  min-width: 0;
  font-weight: 500;
`;

export const RowCount = styled.span`
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: var(--color-text-tertiary);
`;

export const RowIcon = styled.span`
  flex: none;
  width: 18px;
  text-align: center;
  color: var(--color-text-secondary);
`;

export const RowText = styled.span`
  flex: 1;
  min-width: 0;
  display: grid;
  gap: 1px;

  b {
    font-weight: 500;
  }

  span {
    font-size: 12px;
    color: var(--color-text-secondary);
  }
`;

export const Chevron = styled.span`
  flex: none;
  font-size: 11px;
  color: var(--color-text-tertiary);
`;

export const Message = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: var(--color-text-secondary);
  font-size: 14px;
  display: grid;
  gap: 4px;
  justify-items: center;

  strong {
    color: var(--color-text-primary);
    font-weight: 500;
  }
`;

export const Hint = styled.div`
  padding: 36px 12px;
  text-align: center;
  color: var(--color-text-tertiary);
  font-size: 14px;
`;

export const Skeleton = styled.div`
  height: 40px;
  margin: 6px 10px;
  border-radius: var(--radius-sm);
  background: var(--color-bg-secondary);
`;

/* ---------- modal (web >= 1024px) ---------- */

export const ModalLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  min-height: 0;
`;

export const ModalTitle = styled.h2`
  margin: -28px 40px 0 0;
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

export const Columns = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 220px 240px minmax(0, 1fr);
  border-top: 1px solid var(--color-border);
  margin: 0 -24px -24px;
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;

  & + & {
    border-left: 1px solid var(--color-border);
  }
`;

export const ColumnTitle = styled.div`
  flex: none;
  padding: 10px 16px 6px;
  border-bottom: 1px solid var(--color-border);
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-tertiary);
`;

export const ColumnList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 6px 8px 10px;
`;

export const Results = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  border-top: 1px solid var(--color-border);
  margin: 0 -24px -24px;
  padding: 6px 16px 12px;
`;

/* ---------- cascade (web < 1024px) ---------- */

export const CascadePanel = styled.div`
  display: flex;
  flex-direction: column;
  height: min(560px, 72vh);
  margin-top: 6px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-bg-elevated);
  box-shadow: var(--shadow-menu);
  overflow: hidden;
  position: relative;
  z-index: var(--z-dropdown);
`;

export const CascadeTop = styled.div`
  flex: none;
  display: grid;
  gap: 8px;
  padding: 10px 10px 0;
`;

export const Crumbs = styled.nav`
  flex: none;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  padding: 2px 8px 6px;
  border-bottom: 1px solid var(--color-border);
  font-size: 13px;
  color: var(--color-text-secondary);

  button {
    border: 0;
    background: transparent;
    font: inherit;
    color: inherit;
    padding: 4px 6px;
    border-radius: var(--radius-sm);
    cursor: pointer;
  }

  button:hover {
    background: var(--color-bg-secondary);
  }

  button:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  button:disabled {
    opacity: 0.35;
    cursor: default;
  }

  .back {
    width: 32px;
    height: 32px;
    padding: 0;
    color: var(--color-text-primary);
  }

  .current {
    font-weight: 600;
    color: var(--color-text-primary);
    padding: 4px 6px;
  }

  .sep {
    color: var(--color-text-tertiary);
  }
`;

const fadeAtBottom = css`
  mask-image: linear-gradient(to bottom, #000 calc(100% - 36px), transparent);
  -webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - 36px), transparent);
`;

export const CascadeList = styled.div<{ $more: boolean }>`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 6px 8px;
  animation: pickup-slide 150ms ease-out;
  ${({ $more }) => $more && fadeAtBottom}

  @keyframes pickup-slide {
    from {
      transform: translateX(14px);
      opacity: 0.4;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const MoreHint = styled.div`
  flex: none;
  padding: 6px 0 8px;
  border-top: 1px solid var(--color-border);
  text-align: center;
  font-size: 12px;
  color: var(--color-text-tertiary);
`;
